<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const router = useRouter();
const { t } = useI18n();

const agentAssignments = computed(() => [
  {
    key: 'agent_assignment_policy_index',
    title: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.TITLE'),
    description: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.DESCRIPTION'),
    icon: 'i-ph-shuffle-bold',
    features: [
      {
        icon: 'i-ph-arrow-circle-up-bold',
        label: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.FEATURES.0'),
      },
      {
        icon: 'i-ph-scales-bold',
        label: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.FEATURES.1'),
      },
      {
        icon: 'i-ph-tray-bold',
        label: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.FEATURES.2'),
      },
    ],
  },
  {
    key: 'agent_capacity_policy_index',
    title: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.TITLE'),
    description: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.DESCRIPTION'),
    icon: 'i-ph-gauge-bold',
    features: [
      {
        icon: 'i-ph-drop-bold',
        label: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.FEATURES.0'),
      },
      {
        icon: 'i-ph-minus-circle-bold',
        label: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.FEATURES.1'),
      },
      {
        icon: 'i-ph-users-three-bold',
        label: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.FEATURES.2'),
      },
    ],
  },
]);

const handleClick = key => {
  router.push({ name: key });
};
</script>

<template>
  <div class="assignment-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center px-6 flex-shrink-0"
      style="height: 50px"
    >
      <div class="flex items-center gap-2">
        <span class="i-ph-users-four-bold size-5" style="color: #6b7280" />
        <h1 class="text-xl font-semibold" style="color: #111827">
          {{ $t('ASSIGNMENT_POLICY.INDEX.HEADER.TITLE') }}
        </h1>
      </div>
    </header>
    <!-- Description -->
    <div class="ws-desc-bar px-6 py-3">
      <p class="text-sm" style="color: #4b5563">
        {{ $t('ASSIGNMENT_POLICY.INDEX.HEADER.DESCRIPTION') }}
      </p>
    </div>

    <!-- Content -->
    <div class="ws-content flex-1 overflow-y-auto p-6">
      <div class="ap-grid max-w-3xl">
        <div
          v-for="item in agentAssignments"
          :key="item.key"
          class="ap-card group"
          @click="handleClick(item.key)"
        >
          <!-- Card top -->
          <div class="ap-card__top">
            <div class="ap-card__icon">
              <span
                class="size-5"
                :class="[item.icon]"
                style="color: #374151"
              />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="ap-card__title">{{ item.title }}</h3>
              <p class="ap-card__desc">{{ item.description }}</p>
            </div>
            <div class="ap-card__arrow">
              <span class="i-ph-arrow-right-bold size-4" />
            </div>
          </div>
          <!-- Features -->
          <div class="ap-card__features">
            <div
              v-for="(feature, idx) in item.features"
              :key="idx"
              class="ap-feature"
            >
              <div class="ap-feature__icon">
                <span
                  class="size-3.5"
                  :class="[feature.icon]"
                  style="color: #6b7280"
                />
              </div>
              <span class="ap-feature__label">{{ feature.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.assignment-page,
.dark .assignment-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc-bar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-content {
  background: #ffffff;
}

/* Assignment cards grid */
.ap-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.ap-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 20px 24px;
  cursor: pointer;
  transition: all 0.18s ease;
}
.ap-card:hover {
  border-color: #0066ff;
  box-shadow:
    0 0 0 3px rgba(249, 115, 22, 0.08),
    0 4px 12px rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

.ap-card__top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 14px;
}
.ap-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s ease;
}
.ap-card:hover .ap-card__icon {
  background: #fff7ed;
  border-color: #fed7aa;
}
.ap-card:hover .ap-card__icon span {
  color: #0066ff !important;
}

.ap-card__title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px;
  line-height: 1.3;
}
.ap-card__desc {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}

.ap-card__arrow {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #9ca3af;
  transition: all 0.15s ease;
}
.ap-card:hover .ap-card__arrow {
  background: #0066ff;
  color: #ffffff;
  border-color: #0066ff;
}

.ap-card__features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ap-feature {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 6px;
  font-size: 12px;
  color: #4b5563;
}
.ap-feature__icon {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ap-feature__label {
  font-size: 12px;
  color: #4b5563;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
</style>
